/*
  GWP — Page de sélection des cadeaux
  MAJ 28/10/2026 (suite commande FRB2B3106) :
  - Bouton « Complétez votre sélection » / « Cliquez ici pour confirmer votre sélection »
    (classe gwp-confirm-btn--ready = pulsation quand les compteurs sont pleins).
  - Lien « Retourner au panier sans confirmer mes offres » : bulle si des cadeaux
    sont cochés sans être confirmés.
  - À la confirmation : attribut de panier gwp_cadeaux_confirme (horodaté)
    et suppression d'un éventuel gwp_cadeaux_refus.
*/
class GWPSelectionPage extends HTMLElement {

  connectedCallback() {
    // Quotas par palier injectés par la section (x2 si metafield custom.gwp_double)
    this.threshold    = parseInt(this.dataset.threshold)   || 60000;
    this.baseTesteurs = parseInt(this.dataset.maxTesteurs) || 1;
    this.baseMinis    = parseInt(this.dataset.maxMinis)    || 10;
    this.baseSamples  = parseInt(this.dataset.maxSamples)  || 20;
    this.cartUrl      = this.dataset.cartUrl || '/cart';

    this.selection = { testeur: {}, minis: {}, samples: {} };
    this.slots     = 0;

    this._loadCart();
  }

  async _loadCart() {
    try {
      const res  = await fetch('/cart.js');
      this.cart  = await res.json();
      this._computeSlots();
    } catch (e) {
      console.error('[GWP] Erreur chargement panier', e);
    }
  }

  _computeSlots() {
    let total = 0;
    this.freeKeys         = [];
    this.existingTesteurs = 0;
    this.existingMinis    = 0;
    this.existingSamples  = 0;

    for (const item of this.cart.items) {
      const isFree = item.properties && item.properties['_free-gift'] === 'true';
      if (isFree) {
        const cat = item.properties['_free-gift-category'] || '';
        // Seuls les moyens moteurs sont gérés ici.
        // Les offres auto (duo, crackers) ne sont ni comptées ni supprimées.
        if (cat === 'testeur') {
          this.freeKeys.push(item.key);
          this.existingTesteurs += item.quantity;
        } else if (cat === 'minis') {
          this.freeKeys.push(item.key);
          this.existingMinis += item.quantity;
        } else if (cat === 'samples') {
          this.freeKeys.push(item.key);
          this.existingSamples += item.quantity;
        }
      } else {
        total += item.final_line_price;
      }
    }

    this.slots = Math.floor(total / this.threshold);

    if (this.slots === 0) {
      this._showError();
      return;
    }

    this.maxTesteurs = this.baseTesteurs * this.slots;
    this.maxMinis    = this.baseMinis    * this.slots;
    this.maxSamples  = this.baseSamples  * this.slots;

    const slotsAlreadyUsed = Math.max(
      Math.ceil(this.existingTesteurs / this.baseTesteurs),
      Math.ceil(this.existingMinis    / this.baseMinis),
      Math.ceil(this.existingSamples  / this.baseSamples)
    );

    this.slotsRemaining = Math.max(0, this.slots - slotsAlreadyUsed);

    // Mode modification : quota = total slots, on repart de zéro
    // Mode ajout : quota = seulement les nouveaux slots
    if (this.slotsRemaining === 0) {
      this.remainingTesteurs = this.maxTesteurs;
      this.remainingMinis    = this.maxMinis;
      this.remainingSamples  = this.maxSamples;
      this.isModifyMode      = true;
    } else {
      this.remainingTesteurs = this.baseTesteurs * this.slotsRemaining;
      this.remainingMinis    = this.baseMinis    * this.slotsRemaining;
      this.remainingSamples  = this.baseSamples  * this.slotsRemaining;
      this.isModifyMode      = false;
    }

    this._updateQuotaDisplays();
    this._showSlotInfo();
    this._bindEvents();
  }

  _showError() {
    const err  = document.getElementById('gwp-error');
    const cats = document.getElementById('gwp-categories');
    const bar  = document.getElementById('gwp-confirm-bar');
    if (err)  err.style.display  = 'block';
    if (cats) cats.style.display = 'none';
    if (bar)  bar.style.display  = 'none';
  }

  _plural(n, word) {
    return `${n} ${word}${n > 1 ? 's' : ''}`;
  }

  _showSlotInfo() {
    const el = document.getElementById('gwp-slots-indicator');
    const tx = document.getElementById('gwp-slots-text');
    if (!el || !tx) return;
    el.style.display = '';

    if (this.isModifyMode) {
      tx.innerHTML = `<strong>${this.slots} palier${this.slots > 1 ? 's' : ''} atteint${this.slots > 1 ? 's' : ''}</strong> — modifiez votre sélection : ${this._plural(this.maxTesteurs, 'testeur')}, ${this.maxMinis} minis et ${this.maxSamples} échantillons.`;
    } else {
      tx.innerHTML = this.slotsRemaining === 1
        ? `<strong>1 nouveau palier débloqué</strong> — choisissez ${this._plural(this.remainingTesteurs, 'testeur')}, ${this.remainingMinis} minis et ${this.remainingSamples} échantillons supplémentaires.`
        : `<strong>${this.slotsRemaining} nouveaux paliers débloqués</strong> — choisissez ${this._plural(this.remainingTesteurs, 'testeur')}, ${this.remainingMinis} minis et ${this.remainingSamples} échantillons supplémentaires.`;
    }
  }

  _updateQuotaDisplays() {
    const map = {
      testeur: this.remainingTesteurs,
      minis:   this.remainingMinis,
      samples: this.remainingSamples,
    };
    for (const [cat, max] of Object.entries(map)) {
      const counter = document.getElementById(`counter-${cat}`);
      if (counter) counter.querySelector('.gwp-counter-max').textContent = max;
      const recap = document.getElementById(`recap-${cat}-max`);
      if (recap) recap.textContent = max;
    }
  }

  _bindEvents() {
    this.querySelectorAll('.gwp-select-btn').forEach(btn => {
      btn.addEventListener('click', () => this._onSelectClick(btn));
    });

    const confirmBtn = document.getElementById('gwp-confirm-btn');
    if (confirmBtn) confirmBtn.addEventListener('click', () => this._confirm());

    const backLink = document.getElementById('gwp-back-link');
    if (backLink) backLink.addEventListener('click', (e) => this._onBackClick(e));

    const leaveConfirm = document.getElementById('gwp-leave-confirm');
    if (leaveConfirm) leaveConfirm.addEventListener('click', () => {
      this._closeLeaveBubble();
      if (this._isAllFull()) this._confirm();
      else this._scrollToFirstIncomplete();
    });

    const leaveQuit = document.getElementById('gwp-leave-quit');
    if (leaveQuit) leaveQuit.addEventListener('click', () => {
      window.location.href = this.cartUrl;
    });

    this._refreshUI();
  }

  _isAllFull() {
    return this._totalForCategory('testeur') >= this.remainingTesteurs &&
           this._totalForCategory('minis')   >= this.remainingMinis    &&
           this._totalForCategory('samples') >= this.remainingSamples;
  }

  _hasPendingSelection() {
    return ['testeur', 'minis', 'samples'].some(cat => this._totalForCategory(cat) > 0);
  }

  _onBackClick(e) {
    // Rien de coché : retour normal au panier
    if (!this._hasPendingSelection()) return;

    e.preventDefault();
    const bubble = document.getElementById('gwp-leave-bubble');
    if (!bubble) {
      window.location.href = this.cartUrl;
      return;
    }
    const text = bubble.querySelector('p');
    const btn  = document.getElementById('gwp-leave-confirm');

    if (this._isAllFull()) {
      text.textContent = 'Votre sélection n\'est pas confirmée : vous allez perdre vos cadeaux.';
      btn.textContent  = 'Confirmer ma sélection';
    } else {
      text.textContent = 'Votre sélection est incomplète et non confirmée : vous allez perdre vos cadeaux.';
      btn.textContent  = 'Terminer ma sélection';
    }

    // Placer la bulle juste au-dessus de la barre de confirmation
    const bar = document.getElementById('gwp-confirm-bar');
    if (bar) {
      const top = bar.getBoundingClientRect().top;
      bubble.style.bottom = `${Math.max(12, window.innerHeight - top + 12)}px`;
    }
    bubble.hidden = false;
  }

  _closeLeaveBubble() {
    const bubble = document.getElementById('gwp-leave-bubble');
    if (bubble) bubble.hidden = true;
  }

  _scrollToFirstIncomplete() {
    const cat = ['testeur', 'minis', 'samples']
      .find(c => this._totalForCategory(c) < this._maxForCategory(c));
    const el = cat && this.querySelector(`.gwp-sel-category[data-category="${cat}"]`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  _onSelectClick(btn) {
    const card      = btn.closest('.gwp-product-card');
    const category  = btn.dataset.category;
    const variantId = btn.dataset.variantId;

    if (!variantId) return;

    const maxCat  = this._maxForCategory(category);
    const current = this._totalForCategory(category);

    if (!this.selection[category][variantId]) {
      if (current >= maxCat) {
        this._flashCounterFull(category);
        return;
      }
      this.selection[category][variantId] = 1;
      this._showQtyControl(card, variantId, 1);
    }

    this._refreshUI(category);
  }

  _showQtyControl(card, variantId, qty) {
    const btn      = card.querySelector('.gwp-select-btn');
    const control  = card.querySelector('.gwp-qty-control');
    const valEl    = card.querySelector('.gwp-qty-value');
    const imgWrap  = card.querySelector('.gwp-product-img-wrap');
    const category = card.dataset.category;

    btn.style.display     = 'none';
    control.style.display = 'flex';
    valEl.textContent     = qty;
    imgWrap?.classList.add('gwp-selected');

    card.querySelector('.gwp-qty-plus').onclick = () => {
      const total = this._totalForCategory(category);
      if (total >= this._maxForCategory(category)) {
        this._flashCounterFull(category);
        return;
      }
      this.selection[category][variantId]++;
      valEl.textContent = this.selection[category][variantId];
      this._refreshUI(category);
    };

    card.querySelector('.gwp-qty-minus').onclick = () => {
      this.selection[category][variantId]--;
      if (this.selection[category][variantId] <= 0) {
        delete this.selection[category][variantId];
        btn.style.display     = '';
        control.style.display = 'none';
        imgWrap?.classList.remove('gwp-selected');
      } else {
        valEl.textContent = this.selection[category][variantId];
      }
      this._refreshUI(category);
    };
  }

  _maxForCategory(cat) {
    return {
      testeur: this.remainingTesteurs,
      minis:   this.remainingMinis,
      samples: this.remainingSamples,
    }[cat] || 0;
  }

  _totalForCategory(cat) {
    return Object.values(this.selection[cat] || {}).reduce((s, q) => s + q, 0);
  }

  _flashCounterFull(cat) {
    const el = document.getElementById(`counter-${cat}`);
    if (!el) return;
    el.classList.add('gwp-counter--full');
    setTimeout(() => el.classList.remove('gwp-counter--full'), 600);
  }

  _refreshUI(category) {
    const cats = category ? [category] : ['testeur', 'minis', 'samples'];

    for (const cat of cats) {
      const total = this._totalForCategory(cat);
      const max   = this._maxForCategory(cat);

      const counter = document.getElementById(`counter-${cat}`);
      if (counter) {
        counter.querySelector('.gwp-counter-current').textContent = total;
        counter.classList.toggle('gwp-counter--complete', total >= max);
      }

      const recapCount = document.getElementById(`recap-${cat}-count`);
      if (recapCount) recapCount.textContent = total;
      const recapItem = document.getElementById(`recap-${cat}`);
      if (recapItem) recapItem.classList.toggle('gwp-recap-item--done', total >= max);
    }

    const allFull = this._isAllFull();

    const confirmBtn = document.getElementById('gwp-confirm-btn');
    if (confirmBtn) {
      confirmBtn.disabled = !allFull;
      confirmBtn.classList.toggle('gwp-confirm-btn--ready', allFull);
      confirmBtn.textContent = allFull
        ? (confirmBtn.dataset.labelComplete   || 'Cliquez ici pour confirmer votre sélection')
        : (confirmBtn.dataset.labelIncomplete || 'Complétez votre sélection');
    }

    // Si elle modifie sa sélection, la bulle de sortie n'est plus à jour
    this._closeLeaveBubble();
  }

  async _confirm() {
    const confirmBtn = document.getElementById('gwp-confirm-btn');
    if (confirmBtn) {
      confirmBtn.disabled    = true;
      confirmBtn.classList.remove('gwp-confirm-btn--ready');
      confirmBtn.textContent = 'Mise à jour du panier…';
    }

    try {
      // En mode modification : supprimer uniquement les anciens moyens moteurs
      // (freeKeys ne contient que testeur / minis / samples)
      // En mode ajout : garder les existants, ajouter seulement les nouveaux
      if (this.isModifyMode && this.freeKeys.length > 0) {
        const updates = {};
        for (const key of this.freeKeys) updates[key] = 0;
        await fetch('/cart/update.js', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify({ updates }),
        });
      }

      const items = [];
      for (const [cat, selMap] of Object.entries(this.selection)) {
        for (const [variantId, qty] of Object.entries(selMap)) {
          if (qty > 0) {
            items.push({
              id:         parseInt(variantId),
              quantity:   qty,
              properties: {
                '_free-gift':          'true',
                '_free-gift-category': cat,
              },
            });
          }
        }
      }

      if (items.length > 0) {
        const addRes = await fetch('/cart/add.js', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify({ items }),
        });

        if (!addRes.ok) {
          const err = await addRes.json();
          throw new Error(err.description || 'Erreur lors de l\'ajout au panier');
        }
      }

      // Trace : sélection confirmée (une valeur vide efface un éventuel refus précédent)
      try {
        await fetch('/cart/update.js', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify({ attributes: {
            gwp_cadeaux_confirme: new Date().toLocaleString('fr-FR', { timeZone: 'Europe/Paris' }),
            gwp_cadeaux_refus:    '',
          } }),
        });
      } catch (e) {
        console.warn('[GWP] Trace confirmation non enregistrée', e);
      }

      window.location.href = this.cartUrl;

    } catch (e) {
      console.error('[GWP] Erreur confirmation', e);
      alert('Une erreur est survenue : ' + e.message);
      this._refreshUI();
    }
  }
}

customElements.define('gwp-selection-page', GWPSelectionPage);